import axios from "axios";
import API_BASE_URL from "../../../config/apiConfig";

const API_URL = `${API_BASE_URL}/api/RbacAdmin`;

const authHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const handleError = (fn, error) => {
  if (error.response?.data) return error.response.data;
  console.error(`Error en ${fn}:`, error);
  throw error;
};

// ============================================================
// Servicio RBAC Admin
// Controller: RbacAdminController
// Ruta base: /api/RbacAdmin
// Autenticación: JWT Bearer (todos los endpoints)
// Total endpoints: 17
// Documentación: hipaa-logs/Docs/Control/11_ENDPOINTS_RBAC_ADMIN.md
// ============================================================

// ────────────────────────────────────────────────────────────
// SECCIÓN 1: Roles
// ────────────────────────────────────────────────────────────

/**
 * Crea un nuevo rol en el catálogo. La roleKey se normaliza (lowercase + _).
 *
 * @param {Object} data
 * @param {string} data.roleName - Nombre visible del rol (obligatorio)
 * @param {string} data.roleKey - Key estable, se normaliza (obligatorio)
 * @param {string|null} [data.description] - Descripción
 *
 * Ejemplo de request:
 * POST /api/RbacAdmin/roles
 * { "roleName": "Care Coordinator", "roleKey": "care_coordinator", "description": "..." }
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Rol creado exitosamente", "roleId": 12 }
 *
 * Posibles errores (400):
 * { "result": 3, "message": "Ya existe un rol con este nombre" }
 * { "result": 4, "message": "Ya existe un rol con este key" }
 */
export const createRole = async (data) => {
  try {
    const res = await axios.post(`${API_URL}/roles`, data, authHeader());
    return res.data;
  } catch (error) {
    return handleError("createRole", error);
  }
};

/**
 * Actualiza nombre/descripción de un rol. El roleKey es inmutable.
 *
 * @param {Object} data
 * @param {number} data.roleId - ID del rol (obligatorio)
 * @param {string} data.roleName - Nuevo nombre (obligatorio)
 * @param {string|null} [data.description] - Nueva descripción
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Rol actualizado exitosamente" }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Rol no encontrado" }
 * { "result": 3, "message": "Otro rol ya tiene este nombre" }
 */
export const updateRole = async (data) => {
  try {
    const res = await axios.put(`${API_URL}/roles`, data, authHeader());
    return res.data;
  } catch (error) {
    return handleError("updateRole", error);
  }
};

/**
 * Desactiva un rol con cascada (revoca a usuarios y elimina permisos).
 *
 * @param {number} roleId - ID del rol
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Rol desactivado exitosamente",
 *   "usersAffected": 8, "permissionsRemoved": 14 }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Rol no encontrado" }
 * { "result": 2, "message": "El rol ya está desactivado" }
 * { "result": 5, "message": "Los roles del sistema no pueden ser desactivados" }
 */
export const deactivateRole = async (roleId) => {
  try {
    const res = await axios.put(
      `${API_URL}/roles/deactivate`,
      { roleId },
      authHeader()
    );
    return res.data;
  } catch (error) {
    return handleError("deactivateRole", error);
  }
};

/**
 * Reactiva un rol previamente desactivado. SIN cascada — usuarios y
 * permisos NO se restauran automáticamente.
 *
 * @param {number} roleId - ID del rol
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Rol reactivado exitosamente" }
 */
export const reactivateRole = async (roleId) => {
  try {
    const res = await axios.put(
      `${API_URL}/roles/reactivate`,
      { roleId },
      authHeader()
    );
    return res.data;
  } catch (error) {
    return handleError("reactivateRole", error);
  }
};

/**
 * Lista roles con conteos de usuarios y permisos.
 *
 * @param {number|null} [statusFilter] - null = todos, 1 = activos, 0 = inactivos
 *
 * Ejemplo de llamada:
 * GET /api/RbacAdmin/roles?statusFilter=1
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "roleId": 1, "roleName": "Super Administrador", "roleKey": "super_admin",
 *     "description": "Acceso total al sistema", "status": 1, "isSystem": true,
 *     "employeeCreate": 1, "createDate": "2026-01-15T10:30:00",
 *     "employeeUpdate": null, "updateDate": null,
 *     "usersCount": 2, "permissionsCount": 45
 *   }
 * ]
 *
 * Orden: Roles del sistema primero, luego alfabético por nombre.
 */
export const listRoles = async (statusFilter = null) => {
  try {
    const url =
      statusFilter != null
        ? `${API_URL}/roles?statusFilter=${statusFilter}`
        : `${API_URL}/roles`;
    const res = await axios.get(url, authHeader());
    return res.data;
  } catch (error) {
    return handleError("listRoles", error);
  }
};

// ────────────────────────────────────────────────────────────
// SECCIÓN 2: Módulos
// ────────────────────────────────────────────────────────────

/**
 * Crea un nuevo módulo. La moduleKey se normaliza automáticamente.
 *
 * @param {Object} data
 * @param {string} data.moduleName - Nombre visible (obligatorio)
 * @param {string} data.moduleKey - Key estable (obligatorio)
 * @param {string|null} [data.description] - Descripción
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Módulo creado exitosamente", "moduleId": 7 }
 *
 * Posibles errores (400):
 * { "result": 3, "message": "Ya existe un módulo con este nombre" }
 * { "result": 4, "message": "Ya existe un módulo con este key" }
 */
export const createModule = async (data) => {
  try {
    const res = await axios.post(`${API_URL}/modules`, data, authHeader());
    return res.data;
  } catch (error) {
    return handleError("createModule", error);
  }
};

/**
 * Actualiza nombre/descripción de un módulo. La moduleKey es inmutable.
 *
 * @param {Object} data
 * @param {number} data.moduleId - ID (obligatorio)
 * @param {string} data.moduleName - Nuevo nombre (obligatorio)
 * @param {string|null} [data.description] - Nueva descripción
 */
export const updateModule = async (data) => {
  try {
    const res = await axios.put(`${API_URL}/modules`, data, authHeader());
    return res.data;
  } catch (error) {
    return handleError("updateModule", error);
  }
};

/**
 * Desactiva módulo con cascada (todos sus role-permissions se desactivan).
 *
 * @param {number} moduleId
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Módulo desactivado exitosamente", "permissionsRemoved": 22 }
 */
export const deactivateModule = async (moduleId) => {
  try {
    const res = await axios.put(
      `${API_URL}/modules/deactivate`,
      { moduleId },
      authHeader()
    );
    return res.data;
  } catch (error) {
    return handleError("deactivateModule", error);
  }
};

/**
 * Reactiva un módulo. SIN cascada — los permisos NO se restauran.
 *
 * @param {number} moduleId
 */
export const reactivateModule = async (moduleId) => {
  try {
    const res = await axios.put(
      `${API_URL}/modules/reactivate`,
      { moduleId },
      authHeader()
    );
    return res.data;
  } catch (error) {
    return handleError("reactivateModule", error);
  }
};

/**
 * Lista módulos con conteo de role-permissions.
 *
 * @param {number|null} [statusFilter] - null = todos, 1 = activos, 0 = inactivos
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "moduleId": 1, "moduleName": "Asignaciones", "moduleKey": "assignments",
 *     "description": "...", "status": 1,
 *     "employeeCreate": 1, "createDate": "...",
 *     "employeeUpdate": null, "updateDate": null,
 *     "rolePermissionsCount": 18
 *   }
 * ]
 *
 * Orden: alfabético por nombre.
 */
export const listModules = async (statusFilter = null) => {
  try {
    const url =
      statusFilter != null
        ? `${API_URL}/modules?statusFilter=${statusFilter}`
        : `${API_URL}/modules`;
    const res = await axios.get(url, authHeader());
    return res.data;
  } catch (error) {
    return handleError("listModules", error);
  }
};

// ────────────────────────────────────────────────────────────
// SECCIÓN 3: Permisos
// ────────────────────────────────────────────────────────────

/**
 * Crea un nuevo permiso. Los permisos son inmutables (no se pueden desactivar).
 *
 * @param {Object} data
 * @param {string} data.permissionName - Nombre visible (obligatorio)
 * @param {string} data.permissionKey - Key estable (obligatorio)
 * @param {string|null} [data.description] - Descripción
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Permiso creado exitosamente", "permissionId": 5 }
 */
export const createPermission = async (data) => {
  try {
    const res = await axios.post(`${API_URL}/permissions`, data, authHeader());
    return res.data;
  } catch (error) {
    return handleError("createPermission", error);
  }
};

/**
 * Actualiza nombre/descripción de un permiso. La permissionKey es inmutable.
 *
 * @param {Object} data
 * @param {number} data.permissionId
 * @param {string} data.permissionName
 * @param {string|null} [data.description]
 */
export const updatePermission = async (data) => {
  try {
    const res = await axios.put(`${API_URL}/permissions`, data, authHeader());
    return res.data;
  } catch (error) {
    return handleError("updatePermission", error);
  }
};

/**
 * Lista permisos con conteo de uso en role-permissions.
 *
 * @param {number|null} [statusFilter] - null = todos, 1 = activos, 0 = inactivos
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "permissionId": 1, "permissionName": "Create", "permissionKey": "create",
 *     "description": "...", "status": 1,
 *     "employeeCreate": 1, "createDate": "...",
 *     "employeeUpdate": null, "updateDate": null,
 *     "usageCount": 24
 *   }
 * ]
 *
 * Orden: alfabético por nombre.
 */
export const listPermissions = async (statusFilter = null) => {
  try {
    const url =
      statusFilter != null
        ? `${API_URL}/permissions?statusFilter=${statusFilter}`
        : `${API_URL}/permissions`;
    const res = await axios.get(url, authHeader());
    return res.data;
  } catch (error) {
    return handleError("listPermissions", error);
  }
};

// ────────────────────────────────────────────────────────────
// SECCIÓN 4: Asignación Rol ↔ Permiso
// ────────────────────────────────────────────────────────────

/**
 * Otorga un permiso a un rol en un módulo. Si la combinación existía
 * desactivada, la reactiva.
 *
 * @param {Object} data
 * @param {number} data.roleId
 * @param {number} data.moduleId
 * @param {number} data.permissionId
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Permiso otorgado al rol exitosamente",
 *   "rolePermissionId": 234, "isReactivation": false }
 */
export const grantRolePermission = async (data) => {
  try {
    const res = await axios.post(
      `${API_URL}/role-permissions/grant`,
      data,
      authHeader()
    );
    return res.data;
  } catch (error) {
    return handleError("grantRolePermission", error);
  }
};

/**
 * Revoca un permiso a un rol en un módulo (soft-delete).
 *
 * @param {Object} data
 * @param {number} data.roleId
 * @param {number} data.moduleId
 * @param {number} data.permissionId
 */
export const revokeRolePermission = async (data) => {
  try {
    const res = await axios.put(
      `${API_URL}/role-permissions/revoke`,
      data,
      authHeader()
    );
    return res.data;
  } catch (error) {
    return handleError("revokeRolePermission", error);
  }
};

/**
 * Devuelve la matriz completa (módulo activo × permiso activo) de un rol.
 *
 * @param {number} roleId - ID del rol
 *
 * Ejemplo de llamada:
 * GET /api/RbacAdmin/roles/12/permissions
 *
 * Respuesta exitosa (200):
 * {
 *   "result": 0,
 *   "message": "OK",
 *   "role": { "roleId": 12, "roleName": "...", "roleKey": "...", "status": 1 },
 *   "matrix": [
 *     {
 *       "moduleId": 1, "moduleName": "Asignaciones", "moduleKey": "assignments",
 *       "permissionId": 1, "permissionName": "Create", "permissionKey": "create",
 *       "assigned": true, "rolePermissionId": 156,
 *       "assignedDate": "...", "employeeAssigned": 74
 *     }
 *   ]
 * }
 *
 * Si el rol no existe (404):
 * { "result": 1, "message": "Rol no encontrado" }
 */
export const getRolePermissionsMatrix = async (roleId) => {
  try {
    const res = await axios.get(
      `${API_URL}/roles/${roleId}/permissions`,
      authHeader()
    );
    return res.data;
  } catch (error) {
    return handleError("getRolePermissionsMatrix", error);
  }
};

/**
 * Reconcilia masivamente la matriz completa de permisos de un rol.
 * Maneja insert + reactivate + revoke en una sola operación atómica.
 * Idempotente. Si permissions = [], se revocan todos.
 *
 * @param {Object} data
 * @param {number} data.roleId - ID del rol
 * @param {{moduleId:number, permissionId:number}[]} data.permissions - Pares deseados
 *
 * Ejemplo de request:
 * PUT /api/RbacAdmin/role-permissions/bulk
 * {
 *   "roleId": 12,
 *   "permissions": [
 *     { "moduleId": 1, "permissionId": 1 },
 *     { "moduleId": 7, "permissionId": 5 }
 *   ]
 * }
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Permisos del rol actualizados exitosamente",
 *   "inserted": 3, "reactivated": 1, "revoked": 2, "totalChanges": 6 }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Rol no encontrado o inactivo" }
 * { "result": 2, "message": "Uno o más módulos en la lista no existen o están inactivos" }
 * { "result": 3, "message": "Uno o más permisos en la lista no existen o están inactivos" }
 */
export const bulkSetRolePermissions = async (data) => {
  try {
    const res = await axios.put(
      `${API_URL}/role-permissions/bulk`,
      data,
      authHeader()
    );
    return res.data;
  } catch (error) {
    return handleError("bulkSetRolePermissions", error);
  }
};
